import { useCallback } from "react";
import { Navigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { X } from "lucide-react";
import {
  groupByModule,
  moduleNameKey,
  permissionDescriptionKey,
  permissionNameKey,
} from "components/permission-picker/permission-labels";
import {
  fetchPermissionsOverview,
  setUserManualPermissions,
} from "services/permissions.service";
import { PermissionCatalogueEntry, PermissionsPerson } from "types/Permissions";
import { saveErrorKey } from "../save-error";
import {
  ChangeLink,
  EditPanel,
  SettingRow,
  SettingsCard,
  StatusBanner,
  useInlineEdit,
  useSaveWithToast,
} from "../ui";
import { SECONDARY_BUTTON } from "../ui/buttonStyles";
import { useLoadedSetting } from "../use-loaded-setting";

// On a phone and on any touch screen a person's chip is 32px high, and the remove «X» in it, drawn 18px,
// has a 44px tap area: a pseudo-element reaches 13px past the button. The reach must not touch anything
// else: it stops short of the label (14px gap inside the chip) and of the next chip (14px gap between
// chips), and the reaches of two rows (6px past each chip) do not meet in the 14px between rows.
// Tailwind reads class names as text, so each class is written out in full.
const CHIP_HEIGHT = "max-md:min-h-8 [@media(pointer:coarse)]:min-h-8";
const CHIP_LABEL_GAP = "max-md:gap-3.5 [@media(pointer:coarse)]:gap-3.5";
const REMOVE_TAP_AREA =
  "max-md:after:absolute max-md:after:-inset-[13px] [@media(pointer:coarse)]:after:absolute [@media(pointer:coarse)]:after:-inset-[13px]";

// The server's codes for a refused save of a person's rights, and the text each one gets.
const PERMISSION_ERROR_KEYS = {
  LAST_MASTER: "perm_error_last_master",
  STALE_PERMISSIONS: "perm_error_stale",
  USER_NOT_MEMBER: "perm_error_not_member",
  UNKNOWN_PERMISSION: "perm_error_unknown",
};

/** The old "Permission groups" address; groups no longer exist, rights live here. */
export function PermissionsGroupsRedirect() {
  const { companyId } = useParams<{ companyId: string }>();
  return <Navigate to={`/app/${companyId}/settings/permissions`} replace />;
}

/** The rights a person was given by hand (what a save has to send back, plus or minus one). */
function manualKeysOf(person: PermissionsPerson): string[] {
  return person.permissions
    .filter(({ sources }) => sources.some((source) => source.type === "manual"))
    .map(({ key }) => key);
}

function holdsRight(person: PermissionsPerson, key: string): boolean {
  return person.permissions.some((permission) => permission.key === key);
}

type RightRowProps = {
  right: PermissionCatalogueEntry;
  people: PermissionsPerson[];
  /** True while any change is being saved: only one at a time, so none overwrites another. */
  saving: boolean;
  /** Saves the person's whole manual list. */
  onChangeManual: (person: PermissionsPerson, keys: string[]) => Promise<boolean>;
};

/** One right: who holds it and why, and a way to give it to one more person. */
function RightRow({ right, people, saving, onChangeManual }: RightRowProps) {
  const { t } = useTranslation();
  const addPerson = useInlineEdit("");

  const holders = people.filter((person) => holdsRight(person, right.key));
  const candidates = people
    .filter((person) => !holdsRight(person, right.key))
    .sort((a, b) =>
      (a.name || a.email).localeCompare(b.name || b.email, "lt")
    );

  const give = async () => {
    const person = people.find((candidate) => candidate.userId === addPerson.draft);
    if (!person) return;
    const saved = await onChangeManual(person, [...manualKeysOf(person), right.key]);
    if (saved) addPerson.cancel();
  };

  const chips = holders.flatMap((person) => {
    const label = person.name || person.email;
    const sources = person.permissions.find(({ key }) => key === right.key)?.sources ?? [];
    const notActivated = person.isActivated ? null : (
      <span className="font-normal opacity-75"> · {t("perm_not_activated")}</span>
    );
    return sources.map((source) =>
      source.type === "manual" ? (
        <li
          key={`${person.userId}-manual`}
          className={`inline-flex items-center gap-1 ${CHIP_LABEL_GAP} ${CHIP_HEIGHT} rounded-full bg-gray-100 py-0.5 pl-2.5 pr-1 text-xs font-medium text-gray-800`}
        >
          <span>
            {label}
            {notActivated}
          </span>
          <button
            type="button"
            disabled={saving}
            aria-label={t("perm_remove_person", { name: label })}
            onClick={() =>
              onChangeManual(
                person,
                manualKeysOf(person).filter((held) => held !== right.key)
              )
            }
            className={`relative rounded-full p-0.5 text-gray-500 hover:bg-gray-200 hover:text-gray-900 disabled:opacity-60 ${REMOVE_TAP_AREA}`}
          >
            <X className="size-3.5" aria-hidden />
          </button>
        </li>
      ) : (
        <li
          key={`${person.userId}-${source.positionId}`}
          className={`inline-flex items-center ${CHIP_HEIGHT} rounded-full bg-indigo-50 px-2.5 py-0.5 text-xs font-medium text-indigo-800`}
        >
          {label} · {t("perm_source_position", { position: source.positionName })}
          {notActivated}
        </li>
      )
    );
  });

  return (
    <SettingRow
      label={t(permissionNameKey(right.key), { defaultValue: right.key })}
      description={t(permissionDescriptionKey(right.key), { defaultValue: "" })}
      value={
        chips.length > 0 ? (
          <ul className="flex flex-wrap gap-1.5 max-md:gap-3.5 [@media(pointer:coarse)]:gap-3.5">{chips}</ul>
        ) : (
          <span className="text-gray-500">{t("perm_nobody")}</span>
        )
      }
      action={
        !addPerson.editing && (
          <ChangeLink label={t("perm_add_person")} onClick={addPerson.start} />
        )
      }
    >
      {addPerson.editing && (
        <EditPanel saving={saving} onSave={give} onCancel={addPerson.cancel}>
          <label className="block text-sm font-medium text-gray-900">
            {t("perm_choose_person")}
            <select
              value={addPerson.draft}
              onChange={(event) => addPerson.setDraft(event.target.value)}
              className="mt-1 block min-h-11 w-full max-w-md rounded-lg border border-gray-300 px-3 py-2 text-base md:min-h-0 [@media(pointer:coarse)]:min-h-11 md:text-sm"
            >
              <option value="">{t("perm_choose_person_placeholder")}</option>
              {candidates.map((person) => (
                <option key={person.userId} value={person.userId}>
                  {person.name ? `${person.name} (${person.email})` : person.email}
                </option>
              ))}
            </select>
          </label>
        </EditPanel>
      )}
    </SettingRow>
  );
}

/**
 * Who can do what in the company: one card per module, one row per right with
 * the people who hold it. A right from a person's position can only be changed
 * on the position («Pareigos»); a right given by hand is removed here.
 */
export default function PermissionsSection({ companyId }: { companyId: string }) {
  const { t } = useTranslation();
  const request = useCallback(() => fetchPermissionsOverview(companyId), [companyId]);
  const { state, reload } = useLoadedSetting(request);
  const { save, saving } = useSaveWithToast();

  if (state.status === "loading") {
    return <p className="text-sm text-gray-600">{t("loading")}</p>;
  }
  if (state.status === "forbidden") {
    return <p className="text-sm text-gray-600">{t("settings_master_only")}</p>;
  }
  if (state.status === "failed") {
    return (
      <StatusBanner
        tone="error"
        title={t("settings_load_failed")}
        action={
          <button type="button" onClick={reload} className={SECONDARY_BUTTON}>
            {t("retry")}
          </button>
        }
      />
    );
  }

  const { catalogue, people } = state.data;

  // `expected` is the manual list as loaded here: the server refuses the save
  // if someone changed it meanwhile, and then the screen shows what is there now.
  const changeManual = (person: PermissionsPerson, keys: string[]) =>
    save(
      async () => {
        try {
          await setUserManualPermissions(companyId, person.userId, keys, manualKeysOf(person));
        } catch (err) {
          if ((err as any)?.response?.data?.code === "STALE_PERMISSIONS") await reload();
          throw err;
        }
        await reload();
      },
      {
        errorMessage: (err) => t(saveErrorKey(err, PERMISSION_ERROR_KEYS)),
      }
    );

  return (
    <div className="space-y-5">
      <p className="text-sm text-gray-600">{t("perm_position_rights_after_activation")}</p>
      {groupByModule(catalogue).map(({ module, rights }) => (
        <SettingsCard key={module} title={t(moduleNameKey(module), { defaultValue: module })}>
          {rights.map((right) => (
            <RightRow
              key={right.key}
              right={right}
              people={people}
              saving={saving}
              onChangeManual={changeManual}
            />
          ))}
        </SettingsCard>
      ))}
    </div>
  );
}
